/**
 * MultiSelectFilter — generic dropdown that lets the user tick multiple options.
 *   • Stays open while ticking checkboxes
 *   • Closes on outside-click or Esc
 *   • Search box for long lists
 *   • Selected count shown in the trigger pill
 *   • Returns array<string> of selected values; emits onChange whenever selection changes
 */
import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X, Search } from "lucide-react";

const MultiSelectFilter = ({
  label,
  options = [],         // [{value, label}] or [string]
  value = [],
  onChange,
  placeholder = "All",
  searchable = false,
  testid,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Normalize options
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const filtered = q
    ? opts.filter((o) => (o.label || "").toLowerCase().includes(q.toLowerCase()))
    : opts;

  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  const summary = value.length === 0
    ? placeholder
    : value.length === 1
      ? (opts.find((o) => o.value === value[0])?.label || value[0])
      : `${value.length} selected`;

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm border rounded-md transition-colors ${value.length > 0 ? "bg-blue-50 border-blue-300 text-blue-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}
        data-testid={testid}
      >
        <span className="truncate text-left flex-1">{summary}</span>
        <div className="flex items-center gap-1 shrink-0">
          {value.length > 0 && (
            <span
              role="button"
              onClick={clearAll}
              className="hover:bg-blue-200 rounded-full p-0.5"
              title="Clear"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 min-w-[200px] bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col"
          data-testid={testid ? `${testid}-menu` : undefined}
        >
          {searchable && (
            <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  autoFocus
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search…"
                  className="w-full text-sm pl-7 pr-2 py-1 border border-slate-200 rounded outline-none focus:border-blue-400"
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-3">No options</p>
            ) : filtered.map((o) => {
              const selected = value.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left ${selected ? "bg-blue-50 text-blue-900" : "text-slate-700 hover:bg-slate-50"}`}
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                >
                  <span className="truncate">{o.label}</span>
                  {selected && <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
